import React from "react"
import styled, { css } from "styled-components"
import PropTypes from "prop-types"
import { transparentize } from "polished"
import { constants } from "../theme"
import { HeadingSmall } from "./Heading"
import Text from "./Text"
import { Button } from "buoy-core"
import { colors, fontWeight, fontSize } from "buoy-theme"
import CancelLink from "../../modals/components/CancelLink"
import { responsive } from "../../common/styledHelpers"
import { injectIntl } from "react-intl"
import { messages } from "../../chatItemGenerator"

// 4851-todo: update responsive helper

const Paragraph = Text.withComponent("p").extend`
  color: ${props => (props.message ? "white" : "black")};
`

const ContentContainer = styled.div`
  ${props =>
    props.ginaModal
      ? css`
          @media (min-width: 360px) {
            margin: 18px 0px;
          }
          @media (max-width: 360px) {
            margin: 12px 0px;
          }
        `
      : "margin: 18px 0 36px"};
`

const DialogBox = styled.div`
  background-color: ${props => (props.message ? colors.primary : "white")};
  border-radius: ${constants.borderRadius};
  ${props =>
    props.ginaModal
      ? props.showFirstPage
        ? responsive("padding", "12px 40px", "12px 24px")
        : responsive("padding", "44px 40px", "28px 24px")
      : responsive("padding", "40px 40px 28px", "34px 24px 12px")};
  width: 100%;
  max-width: 448px;
  box-shadow: 0 0 8px 0 ${colors.neutral};
  z-index: 9999;
  position: relative;
  margin: auto;
  ${props =>
    props.showCovid19AlertBanner &&
    props.ginaModal &&
    css`
      @media (max-width: 340px) {
        margin: 230px 0 0 0;
      }
      @media (max-width: 375px) and (min-width: 360px) {
        margin: 70px 0 0 0;
      }
    `}
  // NOTE: quick fix to make question feedback modal at least accessible to user on small screens
  ${props =>
    props.showCovid19AlertBanner &&
    props.questionFeedbackModal &&
    css`
      @media (max-width: 340px) {
        margin: 360px 0 0 0;
      }
      @media (max-width: 380px) and (min-width: 340px) {
        margin: 240px 0 0 0;
      }
    `}

  ${props =>
    props.ginaModal && props.showFirstPage
      ? props.showCovid19AlertBanner
        ? css`
            @media (min-width: 380px) {
              height: auto;
            }
            @media (max-width: 380px) {
              height: 410px;
            }
            @media (max-width: 340px) {
              height: auto;
            }
          `
        : css`
            @media (max-width: 326px) {
              height: 475px;
            }
            @media (max-width: 380px) {
              height: 460px;
            }
            @media (min-width: 400px) {
              height: auto;
            }
          `
      : props.showCovid19AlertBanner
      ? props.ginaModal &&
        !props.showFirstPage &&
        css`
          @media (max-width: 360px) {
            height: 395px;
          }
        `
      : "height: auto"}
  ${props =>
    props.ginaModal
      ? css`
          @media (min-width: 411px) {
            height: 450px;
          }
          @media (min-width: 451px) {
            height: 425px;
          }
          @media (min-width: 641px) {
            height: 450px;
          }
        `
      : "height: auto"};
`

const DialogHeading = styled(HeadingSmall)`
  color: ${props => (props.message ? "white" : colors.neutralDarkest)};
  font-weight: ${fontWeight.light};
  ${fontSize("h3")}
`

const ModalBackground = styled.div`
  position: fixed;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => transparentize(0.5, colors.neutralDarkest(props))};
  z-index: 9998;
  padding: 72px 16px 16px;
  overflow: auto;
`

const ButtonRow = styled.div`
  display: inline-flex;
  width: 100%;
  justify-content: flex-start;
  flex-direction: row-reverse;
  flex-wrap: wrap;
`

const ModalCancel = styled(CancelLink)`
  color: ${colors.neutralDarkest};
  font-weight: ${fontWeight.light};
  margin: 0 0 0 24px;
  display: inline-flex;
  align-self: center;
  text-decoration: underline;
  ${fontSize("bodyMain")};
  margin: 0 12px 12px;

  :first-child {
    margin-right: 0;
  }

  :last-child {
    margin-left: 0;
  }
`

const ModalButton = styled(Button)`
  margin: 0 12px 12px;

  :first-child {
    margin-right: 0;
  }

  :last-child {
    margin-left: 0;
  }
`

const getFormattedText = (type, id, formatMessage) => {
  // NOTE: these are the only types currently translated
  // i.e. 'this is confusing' modal is not translated yet so is not formatted here
  switch (type) {
    case "heading":
      if (id === "identifiers.sex.why")
        return formatMessage(messages.identifiersSexWhy)
      else if (id === "identifiers.age.why")
        return formatMessage(messages.identifiersAgeWhy)
      break
    case "body":
      if (id === "identifiers.sex.why.body")
        return formatMessage(messages.identifiersSexWhyBody)
      else if (id === "identifiers.age.why.body")
        return formatMessage(messages.identifiersAgeWhyBody)
      break
    case "button":
      // handles sex and age modals
      if (id === "identifiers.sex.why.okay")
        return formatMessage(messages.identifiersSexWhyOkay)
      break
    case "cancel":
      // handles sex and age modals
      if (id === "identifiers.sex.why.cancel")
        return formatMessage(messages.identifiersSexWhyCancel)
      break
    default: return
  }
}

const Dialog = ({
  heading,
  body,
  buttonText,
  buttonClick,
  cancelClick,
  showSubmit,
  cancelText,
  showCancel,
  intl,
  ...props
}) => (
  <DialogBox {...props}>
    <DialogHeading {...props}>
      {heading && heading.id ? (
       getFormattedText("heading", heading.id, intl.formatMessage)
      ) : (
        heading
      )}
    </DialogHeading>
    <ContentContainer {...props}>
      {/* map in cases where body could be multiple paragraphs and ensure to handle body as string or array */}
      {body && Array.isArray(body) ? (
        body.map(text => <Paragraph {...props}>{text}</Paragraph>)
      ) : (
        <Paragraph {...props}>
          {body && body.id ? (
            getFormattedText("body", body.id, intl.formatMessage)
          ) : (
            body
          )}
        </Paragraph>
      )}
      {props.children}
    </ContentContainer>
    <ButtonRow>
      {showSubmit && (
        <ModalButton
          white={props.message}
          color="primary"
          onClick={buttonClick}
        >
          {buttonText && buttonText.id ? (
            getFormattedText("button", buttonText.id, intl.formatMessage)
          ) : (
            buttonText
          )}
        </ModalButton>
      )}
      {props.extraButtons &&
        props.extraButtons.map((b, i) => (
          <ModalButton
            key={i}
            color={b.negative ? "neutral" : "primary"}
            onClick={b.onClick}
          >
            {b.text}
          </ModalButton>
        ))}
      {showCancel && (
        <ModalCancel onClick={cancelClick}>
          {cancelText && cancelText.id ? (
            getFormattedText("cancel", cancelText.id, intl.formatMessage)
          ) : (
            cancelText
          )}
        </ModalCancel>
      )}
    </ButtonRow>
  </DialogBox>
)

const Modal = props => (
  <ModalBackground onClick={props.cancelClick}>
    <Dialog onClick={e => e.stopPropagation()} {...props} />
  </ModalBackground>
)

Modal.propTypes = {
  heading: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  body: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  buttonText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  buttonClick: PropTypes.func,
  cancelClick: PropTypes.func.isRequired,
  message: PropTypes.bool,
  showSubmit: PropTypes.bool,
}

Modal.defaultProps = {
  showSubmit: true,
  showCancel: true,
  cancelText: messages.identifiersSexWhyCancel,
}

export { Dialog, ModalBackground, DialogBox }
export default injectIntl(Modal)




// WEBPACK FOOTER //
// ./assets/common/components/Modal.js